import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { gql } from '@soundxyz/gql-string';
import { SONG_SLOT_MAX_LENGTH } from '@soundxyz/utils/const';
import {
  ERROR_TYPE,
  MINTING_ERROR_ACTIONS,
  MINTING_INFO_ACTIONS,
  PILLARS,
} from '@soundxyz/utils/constants/logging';
import type { Maybe } from '@soundxyz/utils/helpers';
import { Button } from '@components/main-components/button/Button';
import { Modal } from '@components/main-components/modals/Modal';
import { useAuth } from '@context/auth/base';
import { MediaQuery } from '@context/mediaQuery';
import { ModalContext, ModalType } from '@context/modal';
import { ToastContext } from '@context/toast';
import { Transaction } from '@context/transaction';

import { useMutation, useQuery } from '@graphql/reactQuery';
import {
  SelectSongSlotReleaseDocument,
  SelectSongSlotUsingNftIdDocument,
} from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';
import { useLogInfo } from '@hooks/useLogInfo';
import { useNftOwnershipData } from '@hooks/useNftOwnershipData';
import { DataTestId } from '@utils/test';

import { SelectSlotWrapper } from './SelectSlotWrapper';
import { SetCommentModal } from './SetComment';
import { ShareToSocialModal } from './ShareToSocial';
import { StatusBar } from './StatusBar';
import { COMMENT_SLOT_ALREADY_TAKEN } from './constants';
import { ButtonWrapper, containerStyles, headerStyles, ModalWrapper } from './styles';

/*#__PURE__*/ gql(/* GraphQL */ `
  mutation selectSongSlotUsingNftId($songSlot: Int!, $nftId: UUID!) {
    selectSongSlotUsingNftId(songSlot: $songSlot, nftId: $nftId) {
      __typename
      ... on MutationSelectSongSlotUsingNftIdSuccess {
        data {
          id
          songSlot
        }
      }
      ... on Error {
        message
      }
    }
  }

  query SelectSongSlotRelease($releaseId: UUID!) {
    release(id: $releaseId) {
      id
      claimedSongSlots
      track {
        id
      }
    }
  }
`);

/**
 * A song has only limited slots. We want to encourage users to pick a slot that is not taken.
 * If all slots are taken, we want to allow users to pick a slot that is already taken.
 */
export function canOverwriteSlot({
  totalSlots,
  claimedSlots,
}: {
  /** Number of total available timeslots */
  totalSlots: number;
  /** Number of claimed timeslots */
  claimedSlots: number;
}): boolean {
  return claimedSlots >= totalSlots;
}

type TimeSlotSelectionArgs = { releaseId: string } & (
  | {
      type: 'purchased';
    }
  | {
      type: 'selected';
      selectedNftId: string;
      selectedNftSongSlot: Maybe<number>;
    }
);

const isArtistOnlyPage = false;
const pillar = PILLARS.MINTING;

/**
 * We want to encourage users to pick a slot that is not taken.
 * So we want to show them the slots that are not taken first and not let them pick a slot.
 * Once all slots are taken, we want to allow users to pick a slot that is already taken.
 * So in this component we do a client side unique constraint.
 */
export function SetTimeSlotModal(args: TimeSlotSelectionArgs) {
  // Props and constants
  const { releaseId } = args;
  const existingSongSlot = args.type === 'selected' ? args.selectedNftSongSlot : null;

  // State
  const [songSlot, setSongSlot] = useState<number | null>(
    args.type === 'selected' ? args.selectedNftSongSlot ?? null : null,
  );
  const [isLoading, setIsLoading] = useState(false);

  // Hooks
  const logError = useLogError();
  const logInfo = useLogInfo();
  const { tablet } = MediaQuery.useContainer();
  const { openModal, closeCancelModal } = ModalContext.useContainer();
  const { isAuthenticatedWithAPI } = useAuth();
  const { openToast } = ToastContext.useContainer();
  const { purchasedNftInfo } = Transaction.useContainer();

  const { nftOwnedByUser, refetchOwnedNft, hasPickedTimeslot, tokensOwnedByUserLoading } =
    useNftOwnershipData({ releaseId });

  // GraphQL queries and mutations
  const { mutateAsync: selectSongSlotUsingNftId } = useMutation(SelectSongSlotUsingNftIdDocument, {
    retry: 5,
  });
  const { data } = useQuery(SelectSongSlotReleaseDocument, {
    variables: {
      releaseId,
    },
    staleTime: 0,
  });

  // Derived values
  const release = data?.data.release;
  const claimedSongSlots = useMemo(() => {
    return new Set(release?.claimedSongSlots || []);
  }, [release?.claimedSongSlots]);
  const releaseTrack = release?.track;
  const canOverwrite = canOverwriteSlot({
    claimedSlots: claimedSongSlots.size,
    totalSlots: SONG_SLOT_MAX_LENGTH,
  });
  const nftId =
    args.type === 'selected' ? args.selectedNftId : purchasedNftInfo?.nftId || nftOwnedByUser?.id;

  // Effects
  const openCommentModal = useCallback(() => {
    openModal(ModalType.SET_COMMENT, {
      body: <SetCommentModal releaseId={releaseId} nftId={nftId} />,
    });
  }, [releaseId, nftId, openModal]);

  const openShareModal = useCallback(() => {
    if (!nftId) {
      const message = 'Error skipping commenting, no nftId';
      logError({
        action: MINTING_ERROR_ACTIONS.SET_COMMENT_TIMESLOT_ERROR,
        error: new Error(message),
        errorType: ERROR_TYPE.MISSING_NFT_ID,
        message,
        toast: 'Please refresh and try again',
        level: 'fatal',
        isArtistOnlyPage,
        pillar,
        indexedTags: {
          releaseId,
          songSlot,
        },
      });
      return;
    }

    openModal(ModalType.SHARE_TO_SOCIAL, {
      body: <ShareToSocialModal releaseId={releaseId} nftId={nftId} />,
    });
  }, [nftId, openModal, releaseId, logError, songSlot]);

  useEffect(() => {
    // direct to the comment modal if there is already a song slot selected
    if (existingSongSlot !== null || (nftId === nftOwnedByUser?.id && hasPickedTimeslot)) {
      openCommentModal();
    }
  }, [openCommentModal, existingSongSlot, nftId, nftOwnedByUser?.id, hasPickedTimeslot]);

  useEffect(() => {
    if (!nftId && !tokensOwnedByUserLoading) {
      refetchOwnedNft();
    }
  }, [nftId, refetchOwnedNft, tokensOwnedByUserLoading]);

  // Functions and handlers
  const onSlotClicked = (slot: number) => {
    if (claimedSongSlots.has(slot) && !canOverwrite) {
      // this ensure any previous selection is cleared
      setSongSlot(null);
      return openToast({
        text: COMMENT_SLOT_ALREADY_TAKEN,
        variant: 'error',
      });
    }
    return setSongSlot(slot);
  };

  const handleSetTimeSlot = async () => {
    if (songSlot === undefined || songSlot === null) {
      openToast({
        text: 'Please select a spot on the song to leave your comment',
        variant: 'error',
      });
      return;
    }

    if (!isAuthenticatedWithAPI) {
      openToast({
        text: 'Please Sign in',
        variant: 'error',
      });
      return;
    }

    if (!nftId) {
      const message = 'Error selecting timeslot, no nftId';
      logError({
        action: MINTING_ERROR_ACTIONS.SET_COMMENT_TIMESLOT_ERROR,
        error: new Error(message),
        errorType: ERROR_TYPE.MISSING_NFT_ID,
        message,
        toast: 'Please refresh and try again',
        level: 'fatal',
        isArtistOnlyPage,
        pillar,
        indexedTags: {
          releaseId,
          songSlot,
        },
      });
      return;
    }

    try {
      setIsLoading(true);

      if (nftId) {
        // If we have the NFT id, we can just update the song slot
        const { errors, data } = await selectSongSlotUsingNftId(
          { nftId: nftId, songSlot: songSlot },
          {},
        );

        if (
          data?.selectSongSlotUsingNftId.__typename === 'MutationSelectSongSlotUsingNftIdSuccess'
        ) {
          openCommentModal();
        } else if (data?.selectSongSlotUsingNftId.__typename === 'UniqueConstraintError') {
          const message = 'Unique constraint error';
          logError({
            action: MINTING_ERROR_ACTIONS.SET_COMMENT_TIMESLOT_ERROR,
            error: new Error(message),
            errorType: ERROR_TYPE.UNIQUE_CONSTRAINT_ERROR,
            message,
            toast: 'Comment slot taken! Please select another.',
            level: 'info',
            isArtistOnlyPage,
            pillar,
            indexedTags: {
              releaseId,
              nftId,
              songSlot,
            },
          });
        } else if (typeof data?.selectSongSlotUsingNftId.__typename === 'string') {
          const message = data.selectSongSlotUsingNftId.message;
          logError({
            action: MINTING_ERROR_ACTIONS.SET_COMMENT_TIMESLOT_ERROR,
            error: new Error(message),
            errorType: ERROR_TYPE.UNKNOWN,
            message,
            toast: message,
            level: 'info',
            isArtistOnlyPage,
            pillar,
            indexedTags: {
              releaseId,
              nftId,
              songSlot,
            },
          });
        } else if (errors?.length) {
          logError({
            action: MINTING_ERROR_ACTIONS.SET_COMMENT_TIMESLOT_ERROR,
            error: errors[0], // this comes from apollo errors
            errorType: ERROR_TYPE.UNKNOWN,
            toast: 'Something went wrong',
            message: 'Unknown error during selectSongSlotUsingNftId mutation',
            level: 'fatal',
            isArtistOnlyPage,
            pillar,
            indexedTags: {
              releaseId,
              nftId,
              songSlot,
            },
          });
        } else {
          const message =
            'SetTimeslot has no errors, but also a no success state. This should never happen.';
          logError({
            action: MINTING_ERROR_ACTIONS.SET_COMMENT_TIMESLOT_ERROR,
            error: new Error(message),
            errorType: ERROR_TYPE.UNKNOWN,
            toast: 'Something went wrong',
            message,
            level: 'fatal',
            isArtistOnlyPage,
            pillar,
            indexedTags: {
              releaseId,
              nftId,
              songSlot,
            },
          });
        }
      }
    } catch (error) {
      logError({
        action: MINTING_ERROR_ACTIONS.SET_COMMENT_TIMESLOT_ERROR,
        error,
        errorType: ERROR_TYPE.UNKNOWN,
        toast: 'Something went wrong',
        message: 'Unknown error setting comment timeslot',
        level: 'fatal',
        isArtistOnlyPage,
        pillar,
        indexedTags: {
          releaseId,
          nftId,
          songSlot,
        },
      });
    }

    setIsLoading(false);
    logInfo({
      action: MINTING_INFO_ACTIONS.COMMENT_TIMESLOT_SELECTED,
      message: 'Comment timeslot selected',
      pillar,
      isArtistOnlyPage,
      data: {
        releaseId,
        nftId,
        songSlot,
      },
    });
  };

  if (!releaseTrack) return null;
  const body = (
    <ModalWrapper>
      <StatusBar releaseId={releaseId} />

      <SelectSlotWrapper
        onSlotClicked={onSlotClicked}
        claimedSlots={claimedSongSlots}
        totalSlots={SONG_SLOT_MAX_LENGTH}
        selectedSlot={songSlot}
        trackId={releaseTrack?.id}
        canOverwrite={canOverwrite}
      />

      <ButtonWrapper>
        <Button
          fullWidth={!tablet}
          className="mr-2"
          data-testid={DataTestId.SET_TIMESLOT_MODAL_SKIP_BUTTON}
          label="Skip"
          variant="secondary"
          loading={isLoading || !nftId}
          disabled={isLoading || !nftId}
          onClick={openShareModal}
        />
        <Button
          fullWidth={!tablet}
          data-testid={DataTestId.SET_TIMESLOT_MODAL_NEXT_BUTTON}
          label="Next"
          variant="primary"
          loading={isLoading || !nftId}
          disabled={isLoading || !nftId}
          onClick={handleSetTimeSlot}
        />
      </ButtonWrapper>
    </ModalWrapper>
  );

  return (
    <Modal
      headerText="Select timestamp"
      body={body}
      closeModal={closeCancelModal}
      containerStyles={containerStyles}
      headerStyles={headerStyles}
    />
  );
}
